import React, { useState, useRef, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import Layout from '../components/layout/Layout';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import chatbotService from '../api/chatbotService';
import apiService from '../api/apiService';
import SendIcon from '@mui/icons-material/Send';
import BugReportIcon from '@mui/icons-material/BugReport';
import RefreshIcon from '@mui/icons-material/Refresh';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import projectService from '../api/projectService';
import ReactMarkdown from 'react-markdown';
import rehypeRaw from 'rehype-raw';
import { Highlight, themes } from 'prism-react-renderer';


const ChatbotSimulator = () => {
  const { projectId } = useParams();
  const queryClient = useQueryClient();
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [sessionId, setSessionId] = useState(null);
  const [jwtToken, setJwtToken] = useState('');
  const [useJwtToken, setUseJwtToken] = useState(false);
  const [isLocalMode, setIsLocalMode] = useState(true);
  const [showSettings, setShowSettings] = useState(false);
  const [showTypingEffect, setShowTypingEffect] = useState(false);
  const [typingTimeout, setTypingTimeout] = useState(null);
  const [userData, setUserData] = useState({
    userId: 'test-user-123',
    userName: 'Test User',
    userEmail: 'testuser@example.com',
    metadata: {
      token: '',
      apiKey: '',
      accessToken: '',
      city: '',
      country: '',
    }
  });
  const [error, setError] = useState(null);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  // Debug panel states
  const [showDebugPanel, setShowDebugPanel] = useState(false);
  const [debugInfo, setDebugInfo] = useState(null);
  const [debugLoading, setDebugLoading] = useState(false);
  const [autoRefreshDebug, setAutoRefreshDebug] = useState(true);
  const [debugExpandedSections, setDebugExpandedSections] = useState({
    currentFlow: true,
    variables: true,
    timeline: false,
    stats: false,
    llmDecisions: false
  });

  // Add session initialization guard
  const [isInitializing, setIsInitializing] = useState(false);
  const [hasInitialized, setHasInitialized] = useState(false);
  const initializationRef = useRef(false);

  // Add state to store project SDK config
  const [sdkConfig, setSdkConfig] = useState({
    chatbotName: 'Assistant',
    initialMessage: 'Hi there! How can I help you today?',
    backgroundColor: '#FFFFFF',
    borderColor: '#E5E7EB',
    textColor: '#1F2937',
    fontFamily: 'Inter, sans-serif',
    fontSize: '14px',
    primaryColor: '#3B82F6',
    secondaryColor: '#F3F4F6',
    buttonColor: '#3B82F6',
    buttonTextColor: '#FFFFFF',
    customCss: '',
    placeholderText : 'Type your message...'
  });

  // Fetch API endpoints that use JWT from SDK
  const { data: endpoints, isLoading: isLoadingEndpoints } = useQuery({
    queryKey: ['apiEndpoints', projectId],
    queryFn: () => apiService.getApiEndpoints(projectId),
    onSuccess: (data) => {
      // Check if any endpoints use getFromSdk or useJwtFromSdk (for backward compatibility)
      const hasAuthEndpoints = data?.some(endpoint => endpoint.getFromSdk || endpoint.useJwtFromSdk);
      setUseJwtToken(hasAuthEndpoints);
      
      // Extract auth keys from endpoints with getFromSdk
      if (data) {
        const apiKeys = data
          .filter(endpoint => endpoint.authType === 'apiKey' && endpoint.getFromSdk)
          .map(endpoint => endpoint.authKey)
          .filter(Boolean);
          
        // Pre-populate metadata with required keys
        if (apiKeys.length > 0) {
          setUserData(prev => {
            const newMetadata = { ...prev.metadata };
            apiKeys.forEach(key => {
              if (!newMetadata[key]) {
                newMetadata[key] = '';
              }
            });
            return { ...prev, metadata: newMetadata };
          });
        }
      }
    },
    onError: () => {
      // If there's an error fetching endpoints, assume we might need JWT token anyway
      setUseJwtToken(true);
    }
  });

  // Fetch SDK config
  useEffect(() => {
    const fetchSdkConfig = async () => {
      if (projectId) {
        try {
          const configData = await projectService.getSDKConfig(projectId);
          console.log('Simulator received SDK config:', configData);
          
          // configData should now be the direct config object
          if (configData && typeof configData === 'object') {
            setSdkConfig(prev => ({
              ...prev,
              chatbotName: configData.chatbotName || prev.chatbotName,
              initialMessage: configData.initialMessage || prev.initialMessage,
              backgroundColor: configData.backgroundColor || prev.backgroundColor,
              borderColor: configData.borderColor || prev.borderColor,
              textColor: configData.textColor || prev.textColor,
              fontFamily: configData.fontFamily || prev.fontFamily,
              fontSize: configData.fontSize || prev.fontSize,
              primaryColor: configData.primaryColor || prev.primaryColor,
              secondaryColor: configData.secondaryColor || prev.secondaryColor,
              buttonColor: configData.buttonColor || prev.buttonColor,
              buttonTextColor: configData.buttonTextColor || prev.buttonTextColor,
              customCss: configData.customCss || prev.customCss,
              placeholderText: configData.placeholderText || prev.placeholderText,
            }));
          } else {
            console.warn('Invalid SDK config received in simulator:', configData);
          }
        } catch (error) {
          console.error('Error fetching SDK config in simulator:', error);
        }
      }
    };
    
    fetchSdkConfig();
  }, [projectId]);

  // Add useEffect to check token after endpoints are loaded
  useEffect(() => {
    if (!isLoadingEndpoints && endpoints) {
      // Check if we should enable jwt based on endpoints
      const needsJwt = endpoints.some(endpoint => endpoint.useJwtFromSdk);
      const hasToken = !!jwtToken || !!userData.metadata?.token;
      
      console.log('JWT requirements check:', { 
        needsJwt, 
        hasToken, 
        currentSetting: useJwtToken 
      });
      
      // Auto-enable JWT if we need it and have a token
      if (needsJwt && hasToken && !useJwtToken) {
        console.log('Auto-enabling JWT token usage based on API requirements');
        setUseJwtToken(true);
      }
    }
  }, [endpoints, isLoadingEndpoints, jwtToken, userData.metadata?.token]);

  // Handle useJwtToken toggle
  const handleUseJwtToggle = (e) => {
    const newValue = e.target.checked;
    setUseJwtToken(newValue);
    console.log('JWT token usage toggled:', newValue ? 'ENABLED' : 'DISABLED');
    
    // If enabling and we have a token somewhere, make sure both places have it
    if (newValue) {
      const tokenFromMetadata = userData.metadata?.token;
      const tokenFromState = jwtToken;
      
      if (tokenFromMetadata && !tokenFromState) {
        setJwtToken(tokenFromMetadata);
        console.log('Copied token from metadata to jwtToken state');
      } else if (!tokenFromMetadata && tokenFromState) {
        setUserData(prev => ({
          ...prev,
          metadata: {
            ...prev.metadata,
            token: tokenFromState
          }
        }));
        console.log('Copied token from jwtToken state to metadata');
      }
    }
  };

  // Add event listeners for SSE events
  useEffect(() => {
    // Function to handle intermediate messages from SSE
    const handleIntermediateMessage = (event) => {
      console.log('Received intermediate message event:', event.detail);
      
      if (event.detail && event.detail.message) {
        // Add the intermediate message to the messages state
        setMessages((prev) => [...prev, event.detail.message]);
        
        // Log that we've successfully received and processed an intermediate message
        console.log('Successfully added intermediate message to chat:', event.detail.message);
      } else {
        console.warn('Received malformed intermediate message event:', event.detail);
      }
    };
    
    // Function to handle typing indicator events from SSE
    const handleTypingEvent = (event) => {
      console.log('Received typing indicator event:', event.detail);
      
      // Show the typing indicator
      setShowTypingEffect(true);
      
      // Log the typing indicator state change
      console.log('Typing indicator displayed, state updated to:', true);
    };
    
    // Add event listeners with error handling
    try {
      window.addEventListener('neochat:intermediate', handleIntermediateMessage);
      window.addEventListener('neochat:typing', handleTypingEvent);
      console.log('SSE event listeners attached successfully');
    } catch (error) {
      console.error('Error attaching SSE event listeners:', error);
      setError('Failed to set up event handling for messages');
    }
    
    // Clean up event listeners on unmount
    return () => {
      try {
        window.removeEventListener('neochat:intermediate', handleIntermediateMessage);
        window.removeEventListener('neochat:typing', handleTypingEvent);
        console.log('SSE event listeners removed successfully');
      } catch (error) {
        console.error('Error removing SSE event listeners:', error);
      }
    };
  }, []);

  // Initialize session
  const initSession = async (forceReinit = false) => {
    // Prevent duplicate initialization unless forced
    if (!forceReinit && (isInitializing || (hasInitialized && sessionId) || initializationRef.current)) {
      console.log('Session initialization skipped - already initialized or in progress', {
        isInitializing,
        hasInitialized,
        hasSessionId: !!sessionId,
        refGuard: initializationRef.current
      });
      return;
    }

    try {
      setIsInitializing(true);
      initializationRef.current = true;
      setError(null);
      
      console.log('Starting session initialization...', { forceReinit, projectId });
      
      // Check if there's a token in userData.metadata
      const tokenFromMetadata = userData.metadata?.token;
      if (tokenFromMetadata && !jwtToken) {
        console.log('Found token in userData.metadata, synchronizing with jwtToken state');
        setJwtToken(tokenFromMetadata);
        // If we have API endpoints that need JWT, make sure useJwtToken is enabled
        if (endpoints?.some(endpoint => endpoint.useJwtFromSdk)) {
          setUseJwtToken(true);
        }
      }
      
      // Log JWT token status before initializing
      const effectiveToken = jwtToken || tokenFromMetadata;
      console.log('JWT token status:', {
        tokenProvided: !!effectiveToken,
        tokenSource: jwtToken ? 'jwtToken state' : (tokenFromMetadata ? 'userData.metadata.token' : 'none'),
        useJwtToken: useJwtToken,
        willSendToken: !!effectiveToken && useJwtToken
      });
      
      // If JWT token is available and useJwtToken is true, include it in the session initialization
      const session = await chatbotService.initChatSession(
        projectId, 
        userData,
        useJwtToken ? (effectiveToken || undefined) : undefined
      );
      
      // Check the response from the server
      console.log('Session initialization response:', session);
      
      // Set the session ID
      setSessionId(session.sessionId);
      
      // Set messages from the response
      setMessages(session.messages || []);
      
      // Check if we're in local mode based on the fromBackend flag
      const isBackendAvailable = session.fromBackend === true;
      setIsLocalMode(!isBackendAvailable);
      
      // Mark as successfully initialized
      setHasInitialized(true);
      console.log('Session initialization completed successfully', { sessionId: session.sessionId });
      
      // If we're in local mode, show a message indicating this
      if (!isBackendAvailable) {
        console.warn('Backend connection not detected. Using local simulation mode.');
      } else {
        console.log('Successfully connected to backend API. Using real API mode.');
      }
    } catch (err) {
      console.error('Error initializing chat session:', err);
      setError('Failed to initialize chat session. Using local simulation mode.');
      setIsLocalMode(true);
      
      // Set up a local session anyway
      const localSessionId = 'local-' + Date.now();
      setSessionId(localSessionId);
      setMessages([
        {
          role: 'assistant',
          content: 'Hello! This is a local simulation. The backend is not available.',
          timestamp: new Date()
        }
      ]);
      
      // Still mark as initialized even in error case
      setHasInitialized(true);
      console.log('Session initialization completed with fallback', { sessionId: localSessionId });
    } finally {
      setIsInitializing(false);
      // Keep the ref guard until we're truly done
      setTimeout(() => {
        initializationRef.current = false;
      }, 1000);
    }
  };

  // Initialize session on component mount (only once)
  useEffect(() => {
    if (projectId && !hasInitialized && !isInitializing) {
      console.log('Component mounted, initializing session for projectId:', projectId);
      initSession();
    }
  }, [projectId]); // Only projectId as dependency

  // Send message mutation
  const { mutate: sendMessage, isLoading: isSending } = useMutation({
    mutationFn: async (message) => {
      // Log the start of the message sending process
      console.log('Starting to send message:', message);
      console.log('Current state - isLocalMode:', isLocalMode, 'sessionId:', sessionId);
      
      // If we're in local mode, simulate a response
      if (isLocalMode) {
        console.log('Using local mode simulation for message');
        
        // Simulate network latency
        await new Promise(resolve => setTimeout(resolve, 1500));
        
        // Simulate intermediate message
        const intermediateMessage = {
          role: 'assistant',
          content: 'I\'ll help you with that.',
          timestamp: new Date(),
          isIntermediate: true
        };
        
        // Add intermediate message to messages state
        setMessages((prev) => [...prev, intermediateMessage]);
        console.log('Added simulated intermediate message in local mode');
        
        // Simulate typing effect after intermediate message
        setShowTypingEffect(true);
        console.log('Showing simulated typing effect in local mode');
        
        // Simulate a longer delay for the final response
        await new Promise(resolve => setTimeout(resolve, 2000));
        
        console.log('Returning simulated final response in local mode');
        return {
          message: {
            role: 'assistant',
            content: `This is a simulated response. The backend API is not connected. You said: "${message}"`,
            timestamp: new Date()
          }
        };
      }

      console.log('Using API mode for message processing');
      
      try {
        // In API mode, the typing indicator will be shown via SSE events
        // The intermediate message will also be added via SSE events
        
        // Check if there's a token in userData.metadata to use as fallback
        const tokenFromMetadata = userData.metadata?.token;
        const effectiveToken = jwtToken || tokenFromMetadata;
        
        // Use the real API with additional logging
        console.log('Sending message to API with config:', {
          sessionId,
          projectId,
          messageLength: message.length,
          userDataProvided: !!userData,
          jwtProvided: !!effectiveToken && useJwtToken,
          tokenSource: jwtToken ? 'jwtToken state' : (tokenFromMetadata ? 'userData.metadata.token' : 'none')
        });
        
        // If JWT token is available and useJwtToken is true, ensure it's passed to the backend
        const jwt = useJwtToken ? effectiveToken : undefined;
        console.log('JWT being sent to backend:', jwt ? 'YES (token available)' : 'NO');
        
        return await chatbotService.processMessage({
          sessionId,
          projectId,
          message,
          userData,
          jwt
        });
      } catch (error) {
        console.error('Error in message processing API call:', error);
        // Re-throw the error to be handled by onError
        throw error;
      }
    },
    onSuccess: (data) => {
      console.log('Message sent successfully, response received:', data);
      
      if (data?.message) {
        // We only add the final message here, as the intermediate message 
        // is already added via the SSE event listener
        console.log('Adding final message to chat:', data.message);
        setMessages((prev) => [...prev, data.message]);
      } else {
        console.warn('Unexpected response format:', data);
        // Add a fallback message
        const fallbackMessage = {
          role: 'assistant',
          content: 'Sorry, I encountered an issue processing your request.',
          timestamp: new Date()
        };
        console.log('Adding fallback message due to unexpected response format:', fallbackMessage);
        setMessages((prev) => [...prev, fallbackMessage]);
      }

      // Clear the typing effect
      setShowTypingEffect(false);
      console.log('Typing effect cleared after final message');
      
      // Focus the input field after sending
      setTimeout(() => {
        inputRef.current?.focus();
        console.log('Input field focused');
      }, 0);
    },
    onError: (err) => {
      console.error('Error sending message:', err);
      setError(`Error: ${err.message || 'Failed to send message'}`);
      
      // Hide typing indicator on error
      setShowTypingEffect(false);
      console.log('Typing effect cleared after error');

      // Check if this is a rate limit error
      let errorMessage = 'Sorry, there was an error processing your message. Please try again.';
      
      // Use the custom rate limit message if available
      if (err.isRateLimitError && err.rateLimitMessage) {
        errorMessage = err.rateLimitMessage;
        console.log('Using rate limit error message:', errorMessage);
      } else if (err.response?.status === 429 && err.response?.data?.message) {
        // Alternative way to check for rate limit errors
        errorMessage = err.response.data.message;
        console.log('Using rate limit error message from response data:', errorMessage);
      }

      // Add error message in chat
      const errorResponse = {
        role: 'assistant',
        content: errorMessage,
        timestamp: new Date(),
        isError: true
      };
      console.log('Adding error message to chat:', errorResponse);
      setMessages((prev) => [...prev, errorResponse]);
      setInputValue('');
    }
  });

  // End session mutation
  const { mutate: endSession } = useMutation({
    mutationFn: () => {
      if (isLocalMode) {
        // In local mode, just return a mock response
        return Promise.resolve({ status: 'success' });
      }
      
      return chatbotService.endSession({
        sessionId,
        projectId,
      });
    },
    onSuccess: () => {
      // Reset initialization state and start a new session
      console.log('Session ended successfully, starting new session');
      setHasInitialized(false);
      setSessionId(null);
      setMessages([]);
      initSession(true); // Force new session
    },
    onError: (err) => {
      console.error('Error ending session:', err);
      // Reset state and try to start a new session anyway
      setHasInitialized(false);
      setSessionId(null);
      setMessages([]);
      initSession(true); // Force new session
    }
  });

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Focus on input when chat loads
  useEffect(() => {
    if (!isSending && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isSending, sessionId]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!inputValue.trim() || !sessionId || isInitializing) return;
    
    setError(null);

    // Add user message immediately
    const userMessage = {
      role: 'user',
      content: inputValue,
      timestamp: new Date(),
    };
    
    // Clear input field immediately before adding the message
    const messageToSend = inputValue;
    setInputValue('');
    
    setMessages((prev) => [...prev, userMessage]);

    // Send to backend
    sendMessage(messageToSend);
  };

  const handleEndSession = () => {
    if (sessionId) {
      endSession();
    }
  };

  const handleUserDataChange = (e) => {
    const { name, value } = e.target;
    
    if (name.startsWith('metadata.')) {
      const metadataKey = name.split('.')[1];
      setUserData(prev => ({
        ...prev,
        metadata: {
          ...prev.metadata,
          [metadataKey]: value
        }
      }));
      
      // If this is a token field, also update the jwtToken state
      if (metadataKey === 'token' && useJwtToken) {
        console.log('Token updated in metadata, syncing with jwtToken state');
        setJwtToken(value);
      }
    } else {
      setUserData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  // Handle change of JWT token in the dedicated field
  const handleJwtTokenChange = (e) => {
    const value = e.target.value;
    setJwtToken(value);
    
    // Also update the token in userData.metadata for consistency
    setUserData(prev => ({
      ...prev,
      metadata: {
        ...prev.metadata,
        token: value
      }
    }));
    
    console.log('JWT token updated, synced with userData.metadata.token');
  };

  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  // Debug functions
  const fetchDebugInfo = async () => {
    if (!sessionId || !projectId) {
      console.warn('[Debug] Cannot fetch debug info - missing sessionId or projectId');
      return;
    }
    
    setDebugLoading(true);
    try {
      console.log('[Debug] Fetching debug info for session:', sessionId);
      const info = await chatbotService.getSessionDebugInfo(projectId, sessionId);
      console.log('[Debug] Service returned:', info);
      console.log('[Debug] Type of info:', typeof info);
      console.log('[Debug] Info is null?', info === null);
      console.log('[Debug] Info is undefined?', info === undefined);
      setDebugInfo(info);
      console.log('[Debug] Debug info updated:', info);
    } catch (error) {
      console.error('[Debug] Error fetching debug info:', error);
      setDebugInfo(null);
    } finally {
      setDebugLoading(false);
    }
  };

  const toggleDebugSection = (section) => {
    setDebugExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const formatTimestamp = (timestamp) => {
    return new Date(timestamp).toLocaleString();
  };

  const formatDuration = (seconds) => {
    if (seconds < 60) return `${seconds}s`;
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m ${seconds % 60}s`;
    return `${Math.floor(seconds / 3600)}h ${Math.floor((seconds % 3600) / 60)}m`;
  };

  // Auto-refresh debug info when messages change
  useEffect(() => {
    if (autoRefreshDebug && showDebugPanel && sessionId) {
      console.log('[Debug] Auto-refreshing debug info due to message changes');
      const timeoutId = setTimeout(() => {
        fetchDebugInfo();
      }, 500); // Small delay to allow backend to process
      
      return () => clearTimeout(timeoutId);
    }
  }, [messages, autoRefreshDebug, showDebugPanel, sessionId]);

  // Fetch debug info when debug panel is opened
  useEffect(() => {
    if (showDebugPanel && sessionId && !debugInfo) {
      console.log('[Debug] Debug panel opened, fetching initial debug info');
      fetchDebugInfo();
    }
  }, [showDebugPanel, sessionId]);

  // Helper function to convert hex color to rgb
  const hexToRgb = (hex) => {
    // Remove # if present
    hex = hex.replace('#', '');
    
    // Convert 3-digit hex to 6-digit
    if (hex.length === 3) {
      hex = hex[0] + hex[0] + hex[1] + hex[1] + hex[2] + hex[2];
    }
    
    // Parse the hex values
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);
    
    return `${r}, ${g}, ${b}`;
  };

  return (
    <Layout>
      {/* Add typing animation and message styles */}
      <style>{`
        /* Animation for typing indicator */
        @keyframes typingAnimation {
          0% { opacity: 0.3; transform: translateY(0px); }
          50% { opacity: 1; transform: translateY(-2px); }
          100% { opacity: 0.3; transform: translateY(0px); }
        }
        
        /* Typing indicator */
        .typing-dot {
          animation: typingAnimation 1s infinite ease-in-out;
          margin: 0 4px;
          font-size: 16px;
          color: ${sdkConfig.textColor};
        }
        .typing-dot:nth-child(2) {
          animation-delay: 0.2s;
        }
        .typing-dot:nth-child(3) {
          animation-delay: 0.4s;
        }

        /* Message styling */
        .message {
          display: flex;
          margin-bottom: 16px;
          align-items: flex-start;
        }

        .user-message {
          flex-direction: row-reverse;
        }

        .assistant-message {
          flex-direction: row;
        }

        .avatar {
          width: 36px;
          height: 36px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          margin: 0 8px;
          font-size: 20px;
          flex-shrink: 0;
        }

        .content {
          max-width: 70%;
          position: relative;
        }

        .user-message .content {
          background-color: ${sdkConfig.primaryColor};
          color: ${sdkConfig.buttonTextColor};
          border-radius: 18px 18px 0 18px;
          padding: 12px 16px;
        }

        .assistant-message .content {
          background-color: ${sdkConfig.secondaryColor};
          color: ${sdkConfig.textColor};
          border-radius: 18px 18px 18px 0;
          padding: 12px 16px;
        }

        .timestamp {
          font-size: 12px;
          margin-top: 4px;
          text-align: right;
          opacity: 0.7;
        }

        .typing-indicator-content {
          display: flex;
          align-items: center;
          justify-content: center;
          min-height: 24px;
          min-width: 60px;
          padding: 4px 8px;
        }
        
        /* Send button styling */
        .send-button {
          background-color: ${sdkConfig.buttonColor};
          color: ${sdkConfig.buttonTextColor};
          border-radius: 50%;
          width: 40px;
          height: 40px;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all 0.2s;
        }
        
        .send-button:hover:not(:disabled) {
          background-color: ${sdkConfig.buttonColor === '#3B82F6' ? '#2563EB' : sdkConfig.buttonColor};
          transform: scale(1.05);
        }
        
        .send-button:disabled {
          background-color: #9CA3AF;
          cursor: not-allowed;
        }
        
        .send-button .MuiSvgIcon-root {
          color: ${sdkConfig.buttonTextColor} !important;
          font-size: 1.25rem !important;
        }
        
        /* Improve scrollbar */
        .messages-container::-webkit-scrollbar {
          width: 6px;
        }
        
        .messages-container::-webkit-scrollbar-track {
          background: #F9FAFB;
        }
        
        .messages-container::-webkit-scrollbar-thumb {
          background-color: #D1D5DB;
          border-radius: 6px;
        }
        
        .chat-layout {
          display: flex;
          flex-direction: column;
          height: calc(100vh - 160px);
          font-family: ${sdkConfig.fontFamily};
          font-size: ${sdkConfig.fontSize};
        }
        
        .messages-wrapper {
          flex: 1;
          overflow-y: auto;
          background-color: ${sdkConfig.backgroundColor};
        }
        
        .input-area {
          position: sticky;
          bottom: 0;
          background: ${sdkConfig.backgroundColor};
          border-top: 1px solid ${sdkConfig.borderColor};
          padding: 16px;
          z-index: 10;
        }
        
        .simulator-header {
          background-color: ${sdkConfig.primaryColor};
          color: ${sdkConfig.buttonTextColor};
          padding: 10px 16px;
          font-weight: 600;
          display: flex;
          align-items: center;
          gap: 8px;
        }
        
        /* Markdown Content Styling */
        .markdown-content strong {
          font-weight: bold;
        }
        
        .markdown-content em {
          font-style: italic;
        }
        
        .markdown-content u {
          text-decoration: underline;
        }
        
        .markdown-content h1, 
        .markdown-content h2, 
        .markdown-content h3 {
          margin-top: 0.5rem;
          margin-bottom: 0.5rem;
          font-weight: 600;
        }
        
        .markdown-content h1 {
          font-size: 1.5rem;
        }
        
        .markdown-content h2 {
          font-size: 1.25rem;
        }
        
        .markdown-content h3 {
          font-size: 1.1rem;
        }
        
        .markdown-content p {
          margin-bottom: 0.75rem;
        }
        
        .markdown-content a {
          color: #3b82f6;
          text-decoration: underline;
        }
        
        .markdown-content ul {
          list-style-type: disc;
          margin-left: 1rem;
          margin-bottom: 0.75rem;
        }
        
        .markdown-content ol {
          list-style-type: decimal;
          margin-left: 1rem;
          margin-bottom: 0.75rem;
        }
        
        .markdown-content blockquote {
          border-left: 3px solid #e5e7eb;
          padding-left: 0.75rem;
          font-style: italic;
          margin: 0.75rem 0;
        }
        
        .markdown-content code {
          background-color: #f3f4f6;
          padding: 0.1rem 0.3rem;
          border-radius: 0.25rem;
          font-family: monospace;
        }
        
        .markdown-content pre {
          background-color: #f3f4f6;
          padding: 0.75rem;
          border-radius: 0.25rem;
          overflow-x: auto;
          margin: 0.75rem 0;
        }
        
        .markdown-content table {
          border-collapse: collapse;
          width: 100%;
        }
        
        .markdown-content th,
        .markdown-content td {
          text-align: left;
          padding: 0.5rem;
        }
        
        .markdown-content tr:nth-child(even) {
          background-color: #f9fafb;
        }
        
        .line-number {
          opacity: 0.5;
          font-size: 0.85em;
          user-select: none;
          margin-right: 1rem;
          text-align: right;
          min-width: 2.5em;
          display: inline-block;
        }
        
        /* Custom CSS from SDK config */
        ${sdkConfig.customCss}
      `}</style>
      
      <div className="dashboard">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center">
            <h1 className="page-title">Chatbot Simulator</h1>
            {isLocalMode ? (
              <span className="ml-2 px-2 py-1 bg-yellow-100 text-yellow-800 text-xs font-semibold rounded-full">
                Local Mode
              </span>
            ) : (
              <span className="ml-2 px-2 py-1 bg-green-100 text-green-800 text-xs font-semibold rounded-full">
                API Mode
              </span>
            )}
            {useJwtToken && (jwtToken || userData.metadata?.token) ? (
              <span className="ml-2 px-2 py-1 bg-blue-100 text-blue-800 text-xs font-semibold rounded-full">
                JWT Active
              </span>
            ) : useJwtToken ? (
              <span className="ml-2 px-2 py-1 bg-red-100 text-red-800 text-xs font-semibold rounded-full">
                JWT Enabled (No Token)
              </span>
            ) : null}
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => setShowDebugPanel(!showDebugPanel)}
              className={`flex items-center gap-1 px-3 py-2 rounded-md text-smd font-medium transition-colors ${
                showDebugPanel 
                  ? 'bg-blue-100 text-blue-800 hover:bg-blue-200' 
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              <BugReportIcon sx={{ fontSize: '1.25rem' }} />
              {showDebugPanel ? 'Hide Debug' : 'Debug'}
            </button>
            <button
              onClick={() => setShowSettings(!showSettings)}
              className="flex items-center gap-1 px-3 py-2 bg-gray-100 hover:bg-gray-200 rounded-md text-smd font-medium text-gray-700 transition-colors"
            >
              <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M11.49 3.17c-.38-1.56-2.6-1.56-2.98 0a1.532 1.532 0 01-2.286.948c-1.372-.836-2.942.734-2.106 2.106.54.886.061 2.042-.947 2.287-1.561.379-1.561 2.6 0 2.978a1.532 1.532 0 01.947 2.287c-.836 1.372.734 2.942 2.106 2.106a1.532 1.532 0 012.287.947c.379 1.561 2.6 1.561 2.978 0a1.533 1.533 0 012.287-.947c1.372.836 2.942-.734 2.106-2.106a1.533 1.533 0 01.947-2.287c1.561-.379 1.561-2.6 0-2.978a1.532 1.532 0 01-.947-2.287c.836-1.372-.734-2.942-2.106-2.106a1.532 1.532 0 01-2.287-.947zM10 13a3 3 0 100-6 3 3 0 000 6z" clipRule="evenodd" />
              </svg>
              {showSettings ? 'Hide Settings' : 'Settings'}
            </button>
            <button
              onClick={handleEndSession}
              disabled={!sessionId}
              className="flex items-center gap-1 px-3 py-2 bg-gray-100 hover:bg-gray-200 rounded-md text-smd font-medium text-gray-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed ml-2"
            >
              <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M4 2a1 1 0 011 1v2.101a7.002 7.002 0 0111.601 2.566 1 1 0 11-1.885.666A5.002 5.002 0 005.999 7H9a1 1 0 010 2H4a1 1 0 01-1-1V3a1 1 0 011-1zm.008 9.057a1 1 0 011.276.61A5.002 5.002 0 0014.001 13H11a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0v-2.101a7.002 7.002 0 01-11.601-2.566 1 1 0 01.61-1.276z" clipRule="evenodd" />
              </svg>
              Reset
            </button>
          </div>
        </div>
        
        <div className="flex gap-4">
          {/* Chat container */}
          <div className={`bg-white rounded-lg shadow-md overflow-hidden ${(showSettings || showDebugPanel) ? 'w-68-percent' : 'w-full'}`}>
            <div className="chat-layout">
              {/* Status bar */}
              {(isLocalMode || error) && (
                <div className={`px-4 py-2 text-sm font-medium ${error ? 'bg-red-100 text-red-800' : 'bg-yellow-100 text-yellow-800'}`}>
                  {error || 'Local Simulation Mode - Backend API is not available'}
                </div>
              )}
              
              {/* Chatbot header */}
              <div className="simulator-header text-md">
                {sdkConfig.chatbotName}
              </div>

              {/* Messages */}
              <div className="messages-wrapper p-4 bg-gray-50 messages-container" id="chat-messages">
                {messages.length === 0 ? (
                  <div className="h-full flex items-center justify-center">
                    <div className="text-center text-gray-500">
                      <svg className="h-16 w-16 mx-auto mb-4 text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                      </svg>
                      <h3 className="text-xl font-medium mb-2">No messages yet</h3>
                      <p>Start a conversation by sending a message</p>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-6">
                    {messages.map((message, index) => (
                      <div key={index} className={`message ${message.role === 'user' ? 'user-message' : 'assistant-message'}`}>
                        {/*<div className="avatar">{message.role === 'user' ? '👤' : '🤖'}</div>*/}
                        <div className="content" style={{
                          color: `${message.role === 'user' ? sdkConfig.secondaryColor : sdkConfig.textColor}`,
                          backgroundColor: `${message.role === 'user' ? sdkConfig.primaryColor : 
                                            message.isIntermediate ? 'rgba(' + hexToRgb(sdkConfig.secondaryColor) + ', 0.5)' : sdkConfig.secondaryColor}`,
                          padding: '12px',
                          borderRadius: `${message.role === 'user' ? '16px 16px 0 16px' : '16px 16px 16px 0'}`,
                          maxWidth: '80%',
                          alignSelf: `${message.role === 'user' ? 'flex-end' : 'flex-start'}`,
                          fontSize: sdkConfig.fontSize,
                          fontFamily: sdkConfig.fontFamily,
                          ...(message.isIntermediate ? { fontStyle: 'italic', opacity: 0.9 } : {})
                        }}>
                          <div className="whitespace-pre-wrap break-words markdown-content">
                            <ReactMarkdown 
                              rehypePlugins={[rehypeRaw]}
                              components={{
                                p: ({node, ...props}) => <p className="mb-2 text-smd" {...props} />,
                                h1: ({node, ...props}) => <h1 className="text-xl font-bold mb-2" {...props} />,
                                h2: ({node, ...props}) => <h2 className="text-lg font-bold mb-2" {...props} />,
                                h3: ({node, ...props}) => <h3 className="text-md font-bold mb-2" {...props} />,
                                ul: ({node, ...props}) => <ul className="list-disc pl-6 mb-2" {...props} />,
                                ol: ({node, ...props}) => <ol className="list-decimal pl-6 mb-2" {...props} />,
                                li: ({node, ...props}) => <li className="mb-1" {...props} />,
                                a: ({node, ...props}) => <a className="text-blue-600 underline" target="_blank" rel="noopener noreferrer" {...props} />,
                                blockquote: ({node, ...props}) => <blockquote className="border-l-4 border-gray-300 pl-4 italic" {...props} />,
                                code: ({node, inline, className, children, ...props}) => {
                                  const match = /language-(\w+)/.exec(className || '');
                                  const language = match ? match[1] : 'javascript';
                                  
                                  return !inline ? (
                                    <div className="code-block my-3">
                                      <Highlight
                                        theme={themes.github}
                                        code={String(children).replace(/\n$/, '')}
                                        language={language}
                                      >
                                        {({ className, style, tokens, getLineProps, getTokenProps }) => (
                                          <pre className={`${className} p-3 rounded overflow-auto`} style={style}>
                                            {tokens.map((line, i) => (
                                              <div key={i} {...getLineProps({ line, key: i })}>
                                                <span className="line-number">{i + 1}</span>
                                                {line.map((token, key) => (
                                                  <span key={key} {...getTokenProps({ token, key })} />
                                                ))}
                                              </div>
                                            ))}
                                          </pre>
                                        )}
                                      </Highlight>
                                    </div>
                                  ) : (
                                    <code className="bg-gray-100 px-1 rounded" {...props}>
                                      {children}
                                    </code>
                                  );
                                },
                                table: ({node, ...props}) => <div className="overflow-x-auto my-4"><table className="min-w-full border border-gray-300 rounded" {...props} /></div>,
                                thead: ({node, ...props}) => <thead className="bg-gray-100" {...props} />,
                                tbody: ({node, ...props}) => <tbody className="divide-y divide-gray-300" {...props} />,
                                tr: ({node, ...props}) => <tr className="border-b border-gray-300" {...props} />,
                                th: ({node, ...props}) => <th className="px-4 py-2 text-left font-semibold" {...props} />,
                                td: ({node, ...props}) => <td className="px-4 py-2" {...props} />
                              }}
                            >
                              {message.content}
                            </ReactMarkdown>
                          </div>
                          <div className="timestamp">{formatTime(message.timestamp)}</div>
                        </div>
                      </div>
                    ))}
                    
                    {/* Typing indicator */}
                    {showTypingEffect && (
                      <div className="message assistant-message">
                        <div className="content">
                          <div className="typing-indicator-content">
                            <span className="typing-dot">●</span>
                            <span className="typing-dot">●</span>
                            <span className="typing-dot">●</span>
                          </div>
                        </div>
                      </div>
                    )}
                    
                    <div ref={messagesEndRef} />
                  </div>
                )}
              </div>
              
              {/* Input area */}
              <div className="input-area">
                <form onSubmit={handleSubmit} className="flex items-center space-x-3">
                  <input
                    ref={inputRef}
                    type="text"
                    value={inputValue}
                    onChange={(e) => setInputValue(e.target.value)}
                    placeholder= {sdkConfig.placeholderText}
                    className="flex-1 border border-gray-300 rounded-full px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-smd"
                    style={{
                      flex: 1,
                      padding: '8px 12px',
                      border: `1px solid ${sdkConfig.borderColor}`,
                      borderRadius: '20px',
                      color: sdkConfig.textColor,
                      backgroundColor: sdkConfig.backgroundColor,
                      outline: 'none',
                      width: '100%',
                      marginRight: '8px',
                      fontSize: sdkConfig.fontSize,
                      fontFamily: sdkConfig.fontFamily
                    }}
                    disabled={isSending || !sessionId}
                  />
                  <button
                    type="submit"
                    disabled={isSending || !sessionId || !inputValue.trim() || isInitializing}
                    style = {{
                      backgroundColor: sdkConfig.buttonColor,
                      color: sdkConfig.buttonTextColor,
                      border: 'none',
                      borderRadius: '50%',
                      width: '36px',
                      height: '36px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: 'pointer',
                    }}
                  >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M22 2L11 13" />
                      <path d="M22 2l-7 20-4-9-9-4 20-7z" />
                    </svg>
                  </button>
                </form>
              </div>
            </div>
          </div>
          
          {/* Settings panel */}
          {showSettings && (
            <div className="w-1-3 space-y-4" style={{marginRight : '10px', marginLeft: 'auto'}}>
              <div className="bg-white rounded-lg shadow-md p-4">
                <h2 className="text-lg font-medium text-gray-800 mb-3 flex items-center">
                  <svg className="h-5 w-5 mr-2 text-blue-500" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                  </svg>
                  Session Info
                </h2>
                {sessionId && !isInitializing ? (
                  <div>
                    <div className="mb-3">
                      <p className="text-sm text-gray-500 mb-1">Session ID</p>
                      <p className="font-mono text-xs bg-gray-100 p-2 rounded break-all">{sessionId}</p>
                    </div>
                    <div className="mb-3">
                      <p className="text-sm text-gray-500 mb-1">Mode</p>
                      <p className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${isLocalMode ? 'bg-yellow-100 text-yellow-800' : 'bg-green-100 text-green-800'}`}>
                        {isLocalMode ? 'Local Simulation' : 'Backend Connected'}
                      </p>
                    </div>
                    <div className="mb-3">
                      <p className="text-sm text-gray-500 mb-1">Status</p>
                      <p className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                        Ready
                      </p>
                    </div>
                  </div>
                ) : (
                  <div className="py-4 text-center">
                    <svg className="animate-spin h-5 w-5 text-blue-500 mx-auto mb-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    <p className="text-sm text-gray-600">Initializing session...</p>
                  </div>
                )}
              </div>
              
              <div className="bg-white rounded-lg shadow-md p-4">
                <h2 className="text-lg font-medium text-gray-800 mb-3 flex items-center">
                  <svg className="h-5 w-5 mr-2 text-blue-500" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
                  </svg>
                  User Information
                </h2>
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">User ID</label>
                    <input
                      type="text"
                      name="userId"
                      value={userData.userId}
                      onChange={handleUserDataChange}
                      className="w-full rounded-md border border-gray-300 shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm text-smd"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                    <input
                      type="text"
                      name="userName"
                      value={userData.userName}
                      onChange={handleUserDataChange}
                      className="w-full rounded-md border border-gray-300 shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm text-smd"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                    <input
                      type="email"
                      name="userEmail"
                      value={userData.userEmail}
                      onChange={handleUserDataChange}
                      className="w-full rounded-md border border-gray-300 shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm text-smd"
                    />
                  </div>

                  {/* Add Location Information Section */}
                  <div className="mt-4 border-t pt-3">
                    <h3 className="text-sm font-medium text-gray-700 mb-2">Location Information</h3>
                    <p className="text-xs text-gray-500 mb-3">
                      Add your location to help find nearby places. You can provide either coordinates or an address.
                    </p>
                    
                    <div className="space-y-3">
                      <div className="flex space-x-2">
                        <div className="flex-1">
                          <label className="block text-sm font-medium text-gray-700 mb-1">City</label>
                          <input
                            type="text"
                            name="metadata.city"
                            value={userData.metadata.city || ''}
                            onChange={handleUserDataChange}
                            className="w-full rounded-md border border-gray-300 shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm text-smd"
                            placeholder="e.g., Jakarta"
                          />
                        </div>
                        <div className="flex-1">
                          <label className="block text-sm font-medium text-gray-700 mb-1">Country</label>
                          <input
                            type="text"
                            name="metadata.country"
                            value={userData.metadata.country || ''}
                            onChange={handleUserDataChange}
                            className="w-full rounded-md border border-gray-300 shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm text-smd"
                            placeholder="e.g., Indonesia"
                          />
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Latitude</label>
                        <input
                          type="number"
                          step="any"
                          name="metadata.latitude"
                          value={userData.metadata.latitude || ''}
                          onChange={handleUserDataChange}
                          className="w-full rounded-md border border-gray-300 shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm text-smd"
                          placeholder="e.g., -6.2088"
                        />
                        <p className="text-xs text-gray-500 mt-1">Decimal degrees (e.g., -6.2088 for Jakarta)</p>
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Longitude</label>
                        <input
                          type="number"
                          step="any"
                          name="metadata.longitude"
                          value={userData.metadata.longitude || ''}
                          onChange={handleUserDataChange}
                          className="w-full rounded-md border border-gray-300 shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm text-smd"
                          placeholder="e.g., 106.8456"
                        />
                        <p className="text-xs text-gray-500 mt-1">Decimal degrees (e.g., 106.8456 for Jakarta)</p>
                      </div>
                      
                      <div className="relative">
                        <div className="absolute inset-0 flex items-center" aria-hidden="true">
                          <div className="w-full border-t border-gray-300"></div>
                        </div>
                        <div className="relative flex justify-center text-sm">
                          <span className="px-2 bg-white text-gray-500">OR</span>
                        </div>
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Address</label>
                        <input
                          type="text"
                          name="metadata.address"
                          value={userData.metadata.address || ''}
                          onChange={handleUserDataChange}
                          className="w-full rounded-md border border-gray-300 shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm text-smd"
                          placeholder="e.g., Jl. Jend. Sudirman No.1, Jakarta"
                        />
                        <p className="text-xs text-gray-500 mt-1">Full address (will be geocoded automatically)</p>
                      </div>
                    </div>
                  </div>

                  {endpoints && endpoints.some(endpoint => endpoint.getFromSdk) && (
                    <div className="mt-4 border-t pt-3">
                      <h3 className="text-sm font-medium text-gray-700 mb-2">API Authentication</h3>
                      <p className="text-xs text-gray-500 mb-3">
                        Your project uses API endpoints that get authentication from SDK user data.
                        Enter values for the following fields:
                      </p>
                      
                      {/* Common auth tokens */}
                      <div className="space-y-3">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Bearer/JWT Token</label>
                          <div className="flex items-center mb-2">
                            <input
                              type="checkbox"
                              id="useJwtTokenFromMetadata"
                              checked={useJwtToken}
                              onChange={handleUseJwtToggle}
                              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                            />
                            <label htmlFor="useJwtTokenFromMetadata" className="ml-2 block text-xs text-gray-600">
                              Enable JWT in API calls
                            </label>
                          </div>
                          <input
                            type="password"
                            name="metadata.token"
                            value={userData.metadata.token || ''}
                            onChange={handleJwtTokenChange}
                            className="w-full rounded-md border border-gray-300 shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm text-smd"
                            placeholder="JWT or Bearer token"
                          />
                          <p className="text-xs text-gray-500 mt-1">Used for Bearer and JWT authentication</p>
                        </div>
                        
                        {/* Show fields for API keys based on endpoints */}
                        {endpoints.filter(endpoint => 
                          endpoint.authType === 'apiKey' && 
                          endpoint.getFromSdk && 
                          endpoint.authKey
                        ).map(endpoint => (
                          <div key={endpoint._id}>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              {endpoint.name} API Key ({endpoint.authKey})
                            </label>
                            <input
                              type="password"
                              name={`metadata.${endpoint.authKey}`}
                              value={userData.metadata[endpoint.authKey] || ''}
                              onChange={handleUserDataChange}
                              className="w-full rounded-md border border-gray-300 shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm text-smd"
                              placeholder={`Value for ${endpoint.authKey}`}
                            />
                          </div>
                        ))}
                        
                        {/* Additional common auth fields */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Access Token</label>
                          <input
                            type="password"
                            name="metadata.accessToken"
                            value={userData.metadata.accessToken || ''}
                            onChange={handleUserDataChange}
                            className="w-full rounded-md border border-gray-300 shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm text-smd"
                            placeholder="OAuth access token"
                          />
                        </div>
                      </div>
                    </div>
                  )}
                  
                  <button
                    className="w-full px-4 py-2 mt-2 text-sm font-medium text-white bg-blue-500 rounded-md hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
                    onClick={() => {
                      console.log('Apply Changes clicked - force re-initializing session');
                      setHasInitialized(false);
                      setSessionId(null);
                      setMessages([]);
                      initSession(true); // Force re-initialization
                    }}
                    disabled={isSending || isInitializing}
                  >
                    {isInitializing ? 'Initializing...' : 'Apply Changes'}
                  </button>
                </div>
              </div>
              
              {useJwtToken && !endpoints?.some(endpoint => endpoint.getFromSdk) && (
                <div className="bg-white rounded-lg shadow-md p-4">
                  <h2 className="text-lg font-medium text-gray-800 mb-3 flex items-center">
                    <svg className="h-5 w-5 mr-2 text-blue-500" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M18 8a6 6 0 01-7.743 5.743L10 14l-1 1-1 1H6v-1l1-1 1-1-1-1H6v-1h1.743A6 6 0 1118 8zm-6-4a1 1 0 100 2 2 2 0 012 2 1 1 0 102 0 4 4 0 00-4-4z" clipRule="evenodd" />
                    </svg>
                    JWT Token
                  </h2>
                  <div className="flex items-center mb-3">
                    <input
                      type="checkbox"
                      id="useJwtToken"
                      checked={useJwtToken}
                      onChange={handleUseJwtToggle}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <label htmlFor="useJwtToken" className="ml-2 block text-sm text-gray-900">
                      Enable JWT Token in API Calls
                    </label>
                  </div>
                  <p className="text-sm text-gray-600 mb-2">
                    Your project has API endpoints that use JWT from SDK. 
                    Enter a JWT token to simulate the SDK:
                  </p>
                  <textarea
                    value={jwtToken}
                    onChange={handleJwtTokenChange}
                    className="w-full rounded-md border border-gray-300 shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm font-mono"
                    placeholder="eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9..."
                    rows={4}
                  />
                  <div className="mt-2 text-xs text-gray-500 flex items-center">
                    {jwtToken && useJwtToken ? (
                      <>
                        <svg className="h-4 w-4 mr-1 text-green-500" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                        JWT token will be included in API calls
                      </>
                    ) : (
                      <>
                        <svg className="h-4 w-4 mr-1 text-yellow-500" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                        </svg>
                        {!jwtToken ? "No JWT token provided" : "JWT token provided but not enabled"}
                      </>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
          
          {/* Debug Panel */}
          {showDebugPanel && (
            <div className="w-1/2 space-y-4 max-h-screen overflow-y-auto">
              <div className="bg-white rounded-lg shadow-md p-4">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-medium text-gray-800 flex items-center">
                    <BugReportIcon sx={{ fontSize: '1.25rem', marginRight: '0.5rem', color: '#3B82F6' }} />
                    Debug Panel
                  </h2>
                  <div className="flex items-center gap-2">
                    <label className="flex items-center text-sm text-gray-600">
                      <input
                        type="checkbox"
                        checked={autoRefreshDebug}
                        onChange={(e) => setAutoRefreshDebug(e.target.checked)}
                        className="mr-1 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      Auto-refresh
                    </label>
                    <button
                      onClick={fetchDebugInfo}
                      disabled={debugLoading}
                      className="flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-800 hover:bg-blue-200 rounded text-sm disabled:opacity-50"
                    >
                      <RefreshIcon sx={{ fontSize: '1rem' }} />
                      Refresh
                    </button>
                  </div>
                </div>

                {debugLoading && (
                  <div className="flex items-center justify-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
                    <span className="ml-2 text-gray-600">Loading debug info...</span>
                  </div>
                )}

                {!debugLoading && !debugInfo && sessionId && (
                  <div className="text-center py-8 text-gray-500">
                    <BugReportIcon sx={{ fontSize: '3rem', marginBottom: '1rem', opacity: 0.3 }} />
                    <div className="max-w-md mx-auto">
                      <h3 className="text-lg font-medium text-gray-700 mb-3">Debug Panel</h3>
                      <p className="text-sm mb-4">
                        Debug information will appear here once you start interacting with the chatbot. 
                        The debug panel shows:
                      </p>
                      <div className="text-left bg-gray-50 p-4 rounded-lg mb-4">
                        <ul className="text-xs space-y-1">
                          <li>• Session statistics (messages, costs, duration)</li>
                          <li>• Current flow state and step execution</li>
                          <li>• Session variables and their values</li>
                          <li>• Intent transitions and matching</li>
                          <li>• Execution timeline with all events</li>
                          <li>• API calls and LLM interactions</li>
                          <li>• Error tracking and debugging info</li>
                        </ul>
                      </div>
                      <p className="text-xs mb-4">
                        Send a message to the chatbot to start generating debug information.
                      </p>
                      <button
                        onClick={fetchDebugInfo}
                        className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 text-sm"
                      >
                        Try Loading Debug Info
                      </button>
                    </div>
                  </div>
                )}

                {debugInfo && (
                  <div className="space-y-4">
                    {/* Session Statistics */}
                    <div className="border border-gray-200 rounded-lg">
                      <button
                        onClick={() => toggleDebugSection('stats')}
                        className="w-full px-4 py-2 bg-gray-50 hover:bg-gray-100 flex items-center justify-between text-left font-medium text-gray-800"
                      >
                        📊 Session Statistics
                        {debugExpandedSections.stats ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                      </button>
                      {debugExpandedSections.stats && (
                        <div className="p-4 border-t border-gray-200">
                          <div className="grid grid-cols-2 gap-4 text-sm">
                            <div>
                              <span className="text-gray-600">Total Messages:</span>
                              <span className="ml-2 font-mono">{debugInfo.stats.totalMessages}</span>
                            </div>
                            <div>
                              <span className="text-gray-600">User Messages:</span>
                              <span className="ml-2 font-mono">{debugInfo.stats.userMessages}</span>
                            </div>
                            <div>
                              <span className="text-gray-600">Assistant Messages:</span>
                              <span className="ml-2 font-mono">{debugInfo.stats.assistantMessages}</span>
                            </div>
                            <div>
                              <span className="text-gray-600">System Messages:</span>
                              <span className="ml-2 font-mono">{debugInfo.stats.systemMessages || 0}</span>
                            </div>
                            <div>
                              <span className="text-gray-600">Duration:</span>
                              <span className="ml-2 font-mono">{formatDuration(debugInfo.stats.sessionDuration)}</span>
                            </div>
                            <div>
                              <span className="text-gray-600">Session Status:</span>
                              <span className={`ml-2 px-2 py-1 rounded text-xs ${
                                debugInfo.session.status === 'active' ? 'bg-green-100 text-green-800' :
                                debugInfo.session.status === 'completed' ? 'bg-blue-100 text-blue-800' :
                                'bg-yellow-100 text-yellow-800'
                              }`}>
                                {debugInfo.session.status}
                              </span>
                            </div>
                            <div>
                              <span className="text-gray-600">API Calls:</span>
                              <span className="ml-2 font-mono">{debugInfo.stats.apiCalls}</span>
                            </div>
                            <div>
                              <span className="text-gray-600">LLM Calls:</span>
                              <span className="ml-2 font-mono">{debugInfo.stats.llmCalls}</span>
                            </div>
                            <div>
                              <span className="text-gray-600">Knowledge Searches:</span>
                              <span className="ml-2 font-mono">{debugInfo.stats.knowledgeSearches || 0}</span>
                            </div>
                            <div>
                              <span className="text-gray-600">Intent Transitions:</span>
                              <span className="ml-2 font-mono">{debugInfo.stats.intentTransitions}</span>
                            </div>
                            <div>
                              <span className="text-gray-600">Variables:</span>
                              <span className="ml-2 font-mono">{debugInfo.stats.variableCount}</span>
                            </div>
                            <div>
                              <span className="text-gray-600">Total Cost:</span>
                              <span className="ml-2 font-mono">${debugInfo.stats.totalCost.toFixed(6)}</span>
                            </div>
                          </div>
                          
                          {/* Cost Breakdown */}
                          {debugInfo.costs && (debugInfo.costs.primaryLLM > 0 || debugInfo.costs.secondaryLLM > 0 || debugInfo.costs.apiCalls > 0) && (
                            <div className="mt-4 pt-3 border-t border-gray-100">
                              <h4 className="text-xs font-medium text-gray-700 mb-2">Cost Breakdown:</h4>
                              <div className="grid grid-cols-2 gap-2 text-xs">
                                <div>
                                  <span className="text-gray-600">Primary LLM:</span>
                                  <span className="ml-2 font-mono">${debugInfo.costs.primaryLLM.toFixed(6)}</span>
                                </div>
                                <div>
                                  <span className="text-gray-600">Secondary LLM:</span>
                                  <span className="ml-2 font-mono">${debugInfo.costs.secondaryLLM.toFixed(6)}</span>
                                </div>
                                <div>
                                  <span className="text-gray-600">API Calls:</span>
                                  <span className="ml-2 font-mono">${debugInfo.costs.apiCalls.toFixed(6)}</span>
                                </div>
                                <div>
                                  <span className="text-gray-600">Knowledge Base:</span>
                                  <span className="ml-2 font-mono">${debugInfo.costs.knowledgeBase.toFixed(6)}</span>
                                </div>
                              </div>
                            </div>
                          )}

                          {/* Flow State Summary */}
                          {debugInfo.stats.hasActiveFlow && (
                            <div className="mt-4 pt-3 border-t border-gray-100">
                              <h4 className="text-xs font-medium text-gray-700 mb-2">Flow State:</h4>
                              <div className="bg-blue-50 p-2 rounded text-xs">
                                <span className="text-blue-800">✓ Active flow detected</span>
                                {debugInfo.stats.flowState && (
                                  <div className="mt-1 text-blue-700">
                                    Step {(debugInfo.stats.flowState.currentStepIndex || 0) + 1}, 
                                    Status: {debugInfo.stats.flowState.status}
                                  </div>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Current Flow State */}
                    <div className="border border-gray-200 rounded-lg">
                      <button
                        onClick={() => toggleDebugSection('currentFlow')}
                        className="w-full px-4 py-2 bg-gray-50 hover:bg-gray-100 flex items-center justify-between text-left font-medium text-gray-800"
                      >
                        🔄 Current Flow State
                        {debugExpandedSections.currentFlow ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                      </button>
                      {debugExpandedSections.currentFlow && (
                        <div className="p-4 border-t border-gray-200">
                          {debugInfo.currentFlow ? (
                            <div className="space-y-3">
                              <div>
                                <span className="text-gray-600 text-sm">Active Flow:</span>
                                <div className="mt-1 p-2 bg-blue-50 rounded">
                                  <p className="font-medium text-blue-900">{debugInfo.currentFlow.name}</p>
                                  {debugInfo.currentFlow.description && (
                                    <p className="text-sm text-blue-700 mt-1">{debugInfo.currentFlow.description}</p>
                                  )}
                                </div>
                              </div>
                              
                              {debugInfo.currentFlow.currentStep && (
                                <div>
                                  <span className="text-gray-600 text-sm">Current Step:</span>
                                  <div className="mt-1 p-2 bg-yellow-50 rounded">
                                    <p className="font-medium text-yellow-900">
                                      Step {(debugInfo.currentFlow.currentStepIndex || 0) + 1}: {debugInfo.currentFlow.currentStep.name}
                                    </p>
                                    <p className="text-sm text-yellow-700">Type: {debugInfo.currentFlow.currentStep.type}</p>
                                  </div>
                                </div>
                              )}

                              {debugInfo.currentFlow.linkedIntents && debugInfo.currentFlow.linkedIntents.length > 0 && (
                                <div>
                                  <span className="text-gray-600 text-sm">Linked Intents:</span>
                                  <div className="mt-1 space-y-1">
                                    {debugInfo.currentFlow.linkedIntents.map((intent, index) => (
                                      <div key={index} className="text-xs bg-gray-100 p-2 rounded">
                                        <span className="font-medium">{intent.name}</span>
                                        {intent.description && <span className="text-gray-600 ml-2">- {intent.description}</span>}
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}
                            </div>
                          ) : (
                            <p className="text-gray-500 text-sm">No active flow</p>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Session Variables */}
                    <div className="border border-gray-200 rounded-lg">
                      <button
                        onClick={() => toggleDebugSection('variables')}
                        className="w-full px-4 py-2 bg-gray-50 hover:bg-gray-100 flex items-center justify-between text-left font-medium text-gray-800"
                      >
                        💾 Session Variables ({debugInfo.stats.variableCount})
                        {debugExpandedSections.variables ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                      </button>
                      {debugExpandedSections.variables && (
                        <div className="p-4 border-t border-gray-200">
                          {Object.keys(debugInfo.variables).length > 0 ? (
                            <div className="space-y-2">
                              {Object.entries(debugInfo.variables).map(([name, variable]) => (
                                <div key={name} className="border border-gray-100 rounded p-2">
                                  <div className="flex items-center justify-between">
                                    <span className="font-mono text-sm font-medium text-blue-900">{name}</span>
                                    <span className={`text-xs px-2 py-1 rounded ${
                                      variable.scope === 'session' ? 'bg-green-100 text-green-800' :
                                      variable.scope === 'flow' ? 'bg-blue-100 text-blue-800' :
                                      'bg-yellow-100 text-yellow-800'
                                    }`}>
                                      {variable.scope}
                                    </span>
                                  </div>
                                  <div className="mt-1 text-xs text-gray-600">
                                    <span className="font-mono bg-gray-100 px-1 rounded">{variable.dataType}</span>
                                    {variable.description && <span className="ml-2">{variable.description}</span>}
                                  </div>
                                  <div className="mt-1 p-2 bg-gray-50 rounded font-mono text-xs">
                                    {typeof variable.value === 'object' 
                                      ? JSON.stringify(variable.value, null, 2)
                                      : String(variable.value)
                                    }
                                  </div>
                                  <div className="mt-1 text-xs text-gray-400">
                                    Created: {formatTimestamp(variable.createdAt)}
                                  </div>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <p className="text-gray-500 text-sm">No variables stored</p>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Execution Timeline */}
                    <div className="border border-gray-200 rounded-lg">
                      <button
                        onClick={() => toggleDebugSection('timeline')}
                        className="w-full px-4 py-2 bg-gray-50 hover:bg-gray-100 flex items-center justify-between text-left font-medium text-gray-800"
                      >
                        📋 Execution Timeline ({debugInfo.executionTimeline.length} events)
                        {debugExpandedSections.timeline ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                      </button>
                      {debugExpandedSections.timeline && (
                        <div className="p-4 border-t border-gray-200 max-h-96 overflow-y-auto">
                          <div className="space-y-2">
                            {debugInfo.executionTimeline.slice(-20).reverse().map((event, index) => (
                              <div key={index} className={`border-l-4 pl-3 py-2 ${
                                event.type === 'message' ? 
                                  (event.role === 'user' ? 'border-blue-400 bg-blue-50' : 'border-green-400 bg-green-50') :
                                event.type === 'action' ? 'border-purple-400 bg-purple-50' :
                                'border-orange-400 bg-orange-50'
                              }`}>
                                <div className="flex items-center justify-between">
                                  <span className="text-xs font-medium">
                                    {event.type === 'message' ? `💬 ${event.role}` :
                                     event.type === 'action' ? `⚡ ${event.actionType}` :
                                     '🎯 Intent Transition'}
                                  </span>
                                  <span className="text-xs text-gray-500">
                                    {formatTimestamp(event.timestamp)}
                                  </span>
                                </div>
                                
                                <div className="mt-1 text-sm">
                                  {event.type === 'message' && (
                                    <div>
                                      {event.isIntermediate && <span className="text-xs italic text-gray-600">[Intermediate] </span>}
                                      {event.content}
                                      {event.metadata?.source && (
                                        <div className="text-xs text-gray-600 mt-1">
                                          Source: {event.metadata.source}
                                          {event.metadata.flowName && ` (${event.metadata.flowName})`}
                                        </div>
                                      )}
                                    </div>
                                  )}
                                  
                                  {event.type === 'action' && (
                                    <div>
                                      <span className={event.success ? 'text-green-700' : 'text-red-700'}>
                                        {event.success ? '✅' : '❌'} {event.actionType}
                                      </span>
                                      {event.details && (
                                        <div className="text-xs text-gray-600 mt-1 font-mono">
                                          {event.details.model && `Model: ${event.details.model}`}
                                          {event.details.tokens && ` | Tokens: ${event.details.tokens}`}
                                          {event.details.role && ` | Role: ${event.details.role}`}
                                        </div>
                                      )}
                                    </div>
                                  )}
                                  
                                  {event.type === 'intent_transition' && (
                                    <div>
                                      <div className="text-sm">
                                        {event.fromIntent} → {event.toIntent}
                                      </div>
                                      <div className="text-xs text-gray-600 mt-1">
                                        Confidence: {(event.confidence * 100).toFixed(1)}% | 
                                        Match: {event.matchType}
                                        {event.fromFlow && event.toFlow && (
                                          <div>Flow: {event.fromFlow} → {event.toFlow}</div>
                                        )}
                                      </div>
                                      <div className="text-xs text-gray-500 mt-1 italic">
                                        "{event.userMessage}"
                                      </div>
                                    </div>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>

                    {/* LLM Decisions & Thought Process */}
                    <div className="border border-gray-200 rounded-lg">
                      <button
                        onClick={() => toggleDebugSection('llmDecisions')}
                        className="w-full px-4 py-2 bg-gray-50 hover:bg-gray-100 flex items-center justify-between text-left font-medium text-gray-800"
                      >
                        🧠 LLM Decisions & Thought Process ({debugInfo.llmDecisions?.length || 0})
                        {debugExpandedSections.llmDecisions ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                      </button>
                      {debugExpandedSections.llmDecisions && (
                        <div className="p-4 border-t border-gray-200">
                          {debugInfo.llmDecisions && debugInfo.llmDecisions.length > 0 ? (
                            <div className="space-y-3">
                              {debugInfo.llmDecisions.map((decision, index) => (
                                <div key={index} className={`border rounded-lg p-3 ${
                                  decision.type === 'llm_call' ? 'border-purple-200 bg-purple-50' : 'border-green-200 bg-green-50'
                                }`}>
                                  <div className="flex items-center justify-between mb-2">
                                    <div className="flex items-center space-x-2">
                                      <span className={`text-xs px-2 py-1 rounded font-medium ${
                                        decision.type === 'llm_call' 
                                          ? 'bg-purple-100 text-purple-800' 
                                          : 'bg-green-100 text-green-800'
                                      }`}>
                                        {decision.type === 'llm_call' ? '🤖 LLM Call' : '💭 Response'}
                                      </span>
                                      {decision.role && (
                                        <span className="text-xs px-2 py-1 bg-gray-100 text-gray-700 rounded">
                                          {decision.role}
                                        </span>
                                      )}
                                      {decision.success === false && (
                                        <span className="text-xs px-2 py-1 bg-red-100 text-red-800 rounded">
                                          Failed
                                        </span>
                                      )}
                                    </div>
                                    <span className="text-xs text-gray-500">
                                      {formatTimestamp(decision.timestamp)}
                                    </span>
                                  </div>
                                  
                                  {decision.type === 'llm_call' && (
                                    <div className="space-y-2">
                                      {decision.model && (
                                        <div className="text-sm">
                                          <span className="text-gray-600">Model:</span>
                                          <span className="ml-2 font-mono text-purple-700">{decision.model}</span>
                                        </div>
                                      )}
                                      {decision.purpose && (
                                        <div className="text-sm">
                                          <span className="text-gray-600">Purpose:</span>
                                          <span className="ml-2 text-purple-700">{decision.purpose}</span>
                                        </div>
                                      )}
                                      {decision.tokens && (
                                        <div className="text-sm">
                                          <span className="text-gray-600">Tokens Used:</span>
                                          <span className="ml-2 font-mono text-purple-700">{decision.tokens.toLocaleString()}</span>
                                        </div>
                                      )}
                                      {decision.step !== undefined && (
                                        <div className="text-sm">
                                          <span className="text-gray-600">Step:</span>
                                          <span className="ml-2 font-mono text-purple-700">{decision.step}</span>
                                        </div>
                                      )}
                                    </div>
                                  )}
                                  
                                  {decision.type === 'response_generation' && (
                                    <div className="space-y-2">
                                      {decision.source && (
                                        <div className="text-sm">
                                          <span className="text-gray-600">Source:</span>
                                          <span className="ml-2 text-green-700">{decision.source}</span>
                                        </div>
                                      )}
                                      {decision.flowName && (
                                        <div className="text-sm">
                                          <span className="text-gray-600">Flow:</span>
                                          <span className="ml-2 text-green-700">{decision.flowName}</span>
                                          {decision.stepsExecuted && (
                                            <span className="ml-1 text-xs text-gray-600">
                                              ({decision.stepsExecuted} steps)
                                            </span>
                                          )}
                                        </div>
                                      )}
                                      {decision.wasTransition && decision.fromFlow && (
                                        <div className="text-sm">
                                          <span className="text-gray-600">Transition:</span>
                                          <span className="ml-2 text-orange-700">
                                            {decision.fromFlow} → {decision.flowName}
                                          </span>
                                        </div>
                                      )}
                                      {decision.content && (
                                        <div className="text-sm">
                                          <span className="text-gray-600">Response:</span>
                                          <div className="mt-1 p-2 bg-white border rounded text-sm italic">
                                            "{decision.content.substring(0, 200)}{decision.content.length > 200 ? '...' : ''}"
                                          </div>
                                        </div>
                                      )}
                                    </div>
                                  )}
                                </div>
                              ))}
                            </div>
                          ) : (
                            <div className="text-center py-4 text-gray-500">
                              <span className="text-2xl mb-2 block">🧠</span>
                              <p className="text-sm">No LLM decisions captured yet</p>
                              <p className="text-xs text-gray-400 mt-1">
                                Send messages to see the AI's thought process and decision-making
                              </p>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default ChatbotSimulator; 